import FormInputField from "@/components/lib/from-input-field";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Layout2 from "@/components/Layout/Layout2";
import { Link, useLocation, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { loyaltyLinks } from "@/components/lib/SidebarLinks";
import { Switch } from "@/components/ui/switch";
import { addPointBasedApi, editPointBasedApi } from "@/api/pointBased";
import CreateItem from "@/components/dialog/menu-manage/common-dialog/create-item";
import { useDispatch, useSelector } from "react-redux";
import {
  clearItemVariants,
  removeItemVariant,
  setItemVariants,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { Trash2 } from "lucide-react";
import TableSkeleton from "@/components/skeleton/table-skeletion";
import LoyaltyCreateSkeleton from "@/components/loyalty/LoyaltyCreateSkeleton";
import { fetchLoyaltyTemplates } from "@/redux/slice/loyalty/templeteSlice";
import { Skeleton } from "@/components/ui/skeleton";
import PointTemplate1 from "@/pages/loyalty/templets/card/points/tempate1";
import PointTemplate2 from "@/pages/loyalty/templets/card/points/template2";
import PointTemplate3 from "@/pages/loyalty/templets/card/points/template3";
import PointTemplate4 from "@/pages/loyalty/templets/card/points/template4";
import {
  getComponentByTemplate,
  getTemplateForRecord,
  sortTemplatesBySerial,
} from "@/lib/loyalty-template-utils";

const POINT_TEMPLATE_COMPONENTS = [
  PointTemplate1,
  PointTemplate2,
  PointTemplate3,
  PointTemplate4,
];

function AddPointBasedOffer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updateItem = location.state;
  const { type } = useParams();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const { flatMenus, getLoading: menuLoading } = useSelector(
    (state) => state.menuManage.menu,
  );
  const { templates, getLoading: templateLoading } = useSelector(
    (state) => state.loyalty.template,
  );
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);

  const form = useForm({
    resolver: zodResolver(addNewVoucherSchema),
    mode: "onChange",
    defaultValues: {
      actionType: "add",
      type: "fixed",
      reward: "",
      point_base_card_items: [],
      note: "",
      minimum_required_points: "",
      eligible_reward_points: "",
      template_id: "",
      status: "active",
      terms_and_conditions: false,
      is_conjunction: false,
      template_note: "",
      template_note_enabled: true,
    },
  });

  const isFoodItem = form.watch("type") === "food_item" ? true : false;
  const isPercentage = form.watch("type") === "percentage" ? true : false;
  const isFixed = form.watch("type") === "fixed" ? true : false;
  const pointBaseCardItems = form.watch("point_base_card_items");

  const { reset } = form;

  const giftType = form.watch("type");
  useEffect(() => {
    if (
      giftType === "food_item" &&
      user?.restaurant_id &&
      flatMenus.length === 0
    ) {
      dispatch(fetchAllFlatMenu(user.restaurant_id));
    }
  }, [giftType, user?.restaurant_id, flatMenus.length, dispatch]);

  useEffect(() => {
    // dispatch(fetchAllFlatMenu(user?.restaurant_id));
    if (type !== "update") {
      form.reset();
      dispatch(clearItemVariants());
      return;
    }
    if (updateItem) {
      const {
        type,
        reward,
        point_base_card_items,
        note,
        minimum_required_points,
        eligible_reward_points,
        template_id,
        status,
        template_note,
        terms_and_conditions,
        is_conjunction,
      } = updateItem;
      form.reset({
        actionType: "update",
        type: type || "fixed",
        reward: reward || "",
        point_base_card_items: point_base_card_items || [],
        note: note || "",
        minimum_required_points: minimum_required_points || "",
        eligible_reward_points: eligible_reward_points || "",
        template_id: template_id || "",
        status: status || "active",
        template_note: template_note || "",
        terms_and_conditions: terms_and_conditions || false,
        is_conjunction: is_conjunction || false,
        template_note_enabled: template_note ? true : false,
      });

      dispatch(setItemVariants(point_base_card_items || []));
    }
  }, [updateItem]);

  useEffect(() => {
    if (user?.restaurant_id) {
      dispatch(
        fetchLoyaltyTemplates({
          restaurant_id: user.restaurant_id,
          type: "point",
          per_page: 10,
        }),
      );
    }
  }, [dispatch, user?.restaurant_id]);

  const orderedTemplates = sortTemplatesBySerial(templates || []);
  const selectedTemplateId = form.watch("template_id");
  const selectedTemplate = getTemplateForRecord(
    orderedTemplates,
    selectedTemplateId,
  );
  const SelectedTemplateComponent = getComponentByTemplate(
    selectedTemplate,
    POINT_TEMPLATE_COMPONENTS,
  );

  const handleItemUpdate = async (payload, actionType = "add") => {
    if (!payload) return;
    let allItem = [...itemVariants];
    if (actionType === "add") {
      allItem = [...itemVariants, payload];
    } else if (actionType === "remove") {
      allItem = itemVariants.filter((item) => item.unique_id !== payload);
    }
    form.setValue("point_base_card_items", allItem);
  };
  const handleRemove = async (id) => {
    await dispatch(removeItemVariant(id));
    await handleItemUpdate(id, "remove");
    toast.success("Item removed successfully");
  };

  const addVoucherData = async (data) => {
    setLoading(true);
    const payload = {
      ...data,
      reward: data.type === "food_item" ? null : data.reward,
      restaurant_id: user.restaurant_id,
      point_base_card_items: isFoodItem ? pointBaseCardItems : [],
      template_note: form.watch("template_note_enabled") ? data.template_note : "",
    };

    try {
      let res;

      if (type === "update" && updateItem?.id) {
        res = await editPointBasedApi(payload, updateItem.id);
      } else {
        res = await addPointBasedApi(payload);
      }

      if (res.status === "success") {
        if (type === "update") {
          toast.success("Point based Reward updated successfully!");
        } else {
          toast.success("Point based Reward added successfully!");
        }

        navigate("/loyalty/point-based");
        reset();
        dispatch(clearItemVariants());
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process point based Reward");
    } finally {
      setLoading(false);
    }
  };

  if (menuLoading) return <LoyaltyCreateSkeleton links={loyaltyLinks} />;

  return (
    <Layout2
      pageName={{ title: "Point-Based Reward" }}
      paths={[
        { name: "Loyalty", path: "/loyalty" },
        { name: "Point-Based Reward", path: "/loyalty/point-based" },
        {
          name:
            type === "update"
              ? "Update Point-Based Reward"
              : "Add New Point-Based Reward",
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={loyaltyLinks} />}
    >
      <div className="w-full max-w-5xl mx-auto my-5 mb-10 px-4">
        <Card className="bg-cblack4 text-cwhite">
          <CardHeader>
            <CardTitle className="text-cwhite">
              {form.watch("actionType") === "update"
                ? "Update Point-Based Reward"
                : "Add New Point-Based Reward"}
            </CardTitle>
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-8">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(addVoucherData)}
                  className="flex flex-col gap-8"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormInputField
                      name="minimum_required_points"
                      style="bg-cblack3"
                      placeholder={"Enter minimum required points"}
                      form={form}
                      type="number"
                      label={"Minimum Required Points"}
                      required={true}
                    />
                    <FormInputField
                      name="eligible_reward_points"
                      style="bg-cblack3"
                      placeholder={"Enter eligible reward points"}
                      form={form}
                      type="number"
                      label={"Eligible Reward Points"}
                      required={true}
                    />
                  </div>
                  <FormInputField
                    name="type"
                    style="bg-cblack3"
                    placeholder={"Select reward type"}
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      // { label: "Redeem free item", value: "food_item" },
                      { label: "Redeem Cash Discount", value: "fixed" },
                      { label: "Redeem % Discount", value: "percentage" },
                    ]}
                    label={"Reward type"}
                    required={true}
                  />
                  {isFoodItem && (
                    <div className="border p-5">
                      <div className="flex justify-between items-center gap-5">
                        <h1 className="font-bold">Add Reward</h1>
                        <Button
                          type="button"
                          className="bg-cyellow text-black"
                          onClick={() => setOpenDialog(true)}
                        >
                          Add Reward Item
                        </Button>
                      </div>
                      {itemVariants && itemVariants.length > 0 && (
                        <Table className="mt-4">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item Name</TableHead>
                              <TableHead>Image</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead className="text-right">
                                Action
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {itemVariants && itemVariants.length > 0 ? (
                              itemVariants.map((item, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    {item?.item_name ||
                                      item?.menu_item?.name ||
                                      "Data not available"}
                                  </TableCell>
                                  <TableCell>
                                    {item.file_manager_url ? (
                                      <img
                                        src={item.file_manager_url}
                                        alt={item.item_name}
                                        className="w-12 h-12 rounded"
                                      />
                                    ) : (
                                      "No Image"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {item.price !== undefined
                                      ? `£ ${item.price}`
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <span className="flex items-center justify-end gap-3">
                                      <Trash2
                                        onClick={() =>
                                          handleRemove(item?.unique_id)
                                        }
                                        className="w-4 h-4 text-cred cursor-pointer"
                                      />
                                    </span>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={7} className="text-center">
                                  No data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                  {(isFixed || isPercentage) && (
                    <FormInputField
                      name="reward"
                      style="bg-cblack3"
                      placeholder={"Enter reward amount"}
                      form={form}
                      type="number"
                      label={"Reward"}
                      required={true}
                    />
                  )}
                  <FormInputField
                    name="note"
                    style="bg-cblack3"
                    placeholder={"Enter title"}
                    form={form}
                    type="text"
                    label={"Title"}
                    required={true}
                  />

                  <div>
                    <div className="flex items-center">
                      <Switch
                        checked={form.watch("terms_and_conditions")}
                        onCheckedChange={(val) => {
                          form.setValue("terms_and_conditions", val);
                        }}
                        className="data-[state=unchecked]:bg-neutral-500"
                      />
                      <Label className="ml-2">Terms & Conditions</Label>
                    </div>
                    {form.watch("terms_and_conditions") && (
                      <Card className="bg-cblack text-cwhite mt-2">
                        <CardContent className="pt-5">
                          <div className="mb-3 flex items-center">
                            <Switch
                              checked={form.watch("is_conjunction")}
                              onCheckedChange={(val) => {
                                form.setValue("is_conjunction", val);
                              }}
                              className="data-[state=unchecked]:bg-neutral-500"
                            />
                            <Label className="ml-2">
                              Not conjunction with any other offer
                            </Label>
                          </div>


                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Template Notes separate block (toggleable) */}
                  <div>
                    <div className="flex items-center">
                      <Switch
                        checked={form.watch("template_note_enabled")}
                        onCheckedChange={(val) => {
                          form.setValue("template_note_enabled", val);
                          if (!val) form.setValue("template_note", "");
                        }}
                        className="data-[state=unchecked]:bg-neutral-500"
                      />
                      <Label className="ml-2">Template Notes (Will show on card)</Label>
                    </div>
                    {form.watch("template_note_enabled") && (
                      <Card className="bg-cblack text-cwhite mt-2">
                        <CardContent className="pt-5">
                          <FormInputField
                            name="template_note"
                            style="bg-cblack5"
                            placeholder={"Enter template note"}
                            form={form}
                            label={""}
                            type="textarea"
                          />
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  <div className="space-y-4">
                    <Label>Select Template</Label>

                    {templateLoading ? (
                      <div className="space-y-2">
                        <Skeleton className="h-12 w-full rounded" />
                      </div>
                    ) : (
                      <FormInputField
                        name="template_id"
                        style="bg-cblack7 flex-1"
                        placeholder="Select Template"
                        form={form}
                        type="select"
                        search={true}
                        parseAs="number"
                        required={true}
                        selectOption={
                          orderedTemplates && orderedTemplates.length > 0
                            ? [
                              {
                                label: "Select Template",
                                value: "",
                                disabled: true,
                              },
                              ...orderedTemplates.map((template) => ({
                                label: template.title || "Untitled",
                                value: template.id,
                              })),
                            ]
                            : [
                              {
                                label: "No templates available",
                                value: "",
                                disabled: true,
                              },
                            ]
                        }
                      />
                    )}

                    {SelectedTemplateComponent && selectedTemplate ? (
                      <div className="w-[546px] gap-6">
                        <div className="lg:col-span-1">
                          <SelectedTemplateComponent
                            template={selectedTemplate}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <FormInputField
                    name="status"
                    style="bg-cblack3"
                    placeholder={"Select status"}
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      {
                        label: "Active",
                        value: "active",
                      },
                      { label: "Inactive", value: "inactive" },
                    ]}
                    label={"Status"}
                    required={true}
                  />
                  <div className="mt-4 flex gap-5 w-full">
                    <Link className="w-full" to={"/loyalty/point-based"}>
                      <Button className="w-full bg-cred text-white">
                        Cancel
                      </Button>
                    </Link>

                    <Button isLoading={loading} className="w-full bg-cyellow">
                      Confirm
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
        <CreateItem
          title={"Add Course Item"}
          isOpen={openDialog}
          onClose={() => setOpenDialog(false)}
          onSuccess={(item) => handleItemUpdate(item, "add")}
          quantity={false}
        />
      </div>
    </Layout2>
  );
}

export default AddPointBasedOffer;

const addNewVoucherSchema = z
  .object({
    reward: z.coerce.number().optional(),
    type: z.string().min(1, "Reward type is required"),
    note: z.string().min(1, "Note is required"),
    status: z.string().min(1, "Status is required"),
    template_id: z.coerce.number().min(1, "Template is required"),
    minimum_required_points: z.coerce
      .number()
      .min(1, "Minimum required is required"),
    eligible_reward_points: z.coerce
      .number()
      .min(1, "Eligible reward is required"),
    terms_and_conditions: z.boolean().optional(),
    is_conjunction: z.boolean().optional(),
    template_note: z.string().optional(),
    template_note_enabled: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type !== "food_item") {
      if (!data.reward || data.reward < 1) {
        ctx.addIssue({
          path: ["reward"],
          message: "Reward is required",
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });
